/**
 * Turn any error-shaped value into one human-readable string.
 *
 * The guard this exists for: a Zod `flatten()` result — an object with the keys
 * `{ formErrors, fieldErrors }` — reaches a string-only sink (a `toast.error`,
 * a thrown `Error`, or a JSX child) and React refuses the object as a child,
 * which throws minified error #31 and unmounts the page. More than 200 API
 * routes return `parsed.error.flatten()` on a 400, so every client read of a
 * response `error` field must pass through this extractor, never straight into
 * a sink.
 *
 * Pure module — no server or React imports — so both client components and the
 * server helper in `lib/api-error.ts` can share the flatten formatter.
 */
import { SSO_REQUIRED_ERROR } from "@/lib/sso/constants";
import {
  accountReadOnlyMessage,
  isAccountReadOnlyError,
  type AccountReadOnlyReason,
} from "@/lib/account-read-only";

/**
 * What a person sees instead of the `sso_required` wire code (403 from
 * `requireAuth()` / `ssoWebGateResponse`). Mapped once here so every client
 * sink that routes a response `error` through getErrorMessage shows the same
 * sentence as the /sso-required page (#2722 item 5).
 */
export const SSO_REQUIRED_MESSAGE =
  "Your organisation requires single sign-on. Sign out, then sign back in with your work email.";

/**
 * Collect the readable messages from a Zod `flatten()` result, in order:
 * form-level errors first, then each field error prefixed with its field name.
 * Every field is re-checked at runtime, so an unrelated object yields `[]`.
 */
export function flattenedZodMessages(value: {
  formErrors?: unknown;
  fieldErrors?: unknown;
}): string[] {
  const messages: string[] = [];

  if (Array.isArray(value.formErrors)) {
    for (const message of value.formErrors) {
      if (typeof message === "string" && message.trim()) messages.push(message);
    }
  }

  const fieldErrors = value.fieldErrors;
  if (fieldErrors && typeof fieldErrors === "object") {
    for (const [field, errors] of Object.entries(fieldErrors)) {
      if (!Array.isArray(errors)) continue;
      for (const message of errors) {
        if (typeof message === "string" && message.trim()) {
          messages.push(`${field}: ${message}`);
        }
      }
    }
  }

  return messages;
}

/**
 * Reduce any value to a string safe to show a user, falling back to `fallback`
 * for a non-string, a blank string, or an object that carries no readable
 * message (including a non-Zod object).
 */
export function getErrorMessage(value: unknown, fallback: string): string {
  if (value === SSO_REQUIRED_ERROR) return SSO_REQUIRED_MESSAGE;
  // The web write-state refusal (403 `code: ACCOUNT_READ_ONLY` from
  // requireAuth(), #2716): a sink that forwarded the whole body gets its
  // reason-specific `message`; one that forwarded the legacy bare token gets
  // the generic dunning sentence. (A sink that forwarded `data.error` already
  // holds the sentence itself — the body carries it in `error`.)
  if (isAccountReadOnlyError(value)) {
    if (value && typeof value === "object") {
      const { message, reason } = value as {
        message?: unknown;
        reason?: AccountReadOnlyReason;
      };
      if (typeof message === "string" && message.trim()) return message;
      return accountReadOnlyMessage(reason);
    }
    return accountReadOnlyMessage(null);
  }
  if (typeof value === "string") return value.trim() ? value : fallback;
  if (value instanceof Error) return value.message.trim() ? value.message : fallback;
  if (value && typeof value === "object") {
    // The mobile error envelope (`buildErrorEnvelope` — `{ code, message }`,
    // e.g. the shared storage-reclaim 409): the human message is `message`,
    // not Zod's flatten shape.
    const { message } = value as { message?: unknown };
    if (typeof message === "string" && message.trim()) return message;
    const messages = flattenedZodMessages(value as { formErrors?: unknown; fieldErrors?: unknown });
    return messages.length ? messages.join(". ") : fallback;
  }
  return fallback;
}

/**
 * The read-only sentence for a parsed 403 body when it is the account
 * write-state refusal, else null. For the client sinks that give a 403 their
 * OWN meaning (upgrade prompt, "no longer the approver", role revoked): check
 * this FIRST so a read-only account is told the truth instead (#2716 F7).
 */
export function accountReadOnlyMessageOrNull(body: unknown): string | null {
  return isAccountReadOnlyError(body)
    ? getErrorMessage(body, accountReadOnlyMessage(null))
    : null;
}
